import { datadogRum } from '@datadog/browser-rum';
import { config } from '@iheartjane/config';
import { initializeApi } from '@iheartjane/data-access';
import { asyncFlagProvider, getFlagContext } from '@iheartjane/feature-flags';
import { initTrackJS } from '@iheartjane/tracking';
import { createRoot } from 'react-dom/client';

import { ErrorBoundary } from '@jane/shared-ecomm/components';
import { EcommAppProvider } from '@jane/shared/providers';
import { initMixpanel } from '@jane/shared/util';

import { App } from './app/App';
import { registerJaneServiceWorker } from './register';

initMixpanel(config.kioskMixpanelCustomerToken, {
  useLocalStorage: true,
});
initTrackJS({
  application: config.trackJsApplication.replace('jane', 'kiosk'),
  forwardingDomain: config.trackJsDomain,
  token: config.trackJsToken,
  version: config.appVersion,
});
initializeApi({ baseUrl: config.apiPathBase });

// eslint-disable-next-line @iheartjane/workspace/no-dom-globals-in-module-scope
const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element not found');
}
const root = createRoot(container);
const debugServiceWorker = process.env['NODE_ENV'] !== 'production';

(async () => {
  await registerJaneServiceWorker({
    onVersionFetched: (serverVersion) => {
      const clientUrl = new URL(window.location.href);
      /**
       * We think it is safe to reload if we are on the landing page, or the menu
       * page that has been reloaded from the idle timer
       * see useResetKiosk > clientReload for implementation
       */
      const safeToReload = clientUrl.pathname.includes('landing');

      // if the versions don't match and we're on the landing page
      if (serverVersion !== config.buildVersion && safeToReload) {
        // check if we haven't already reloaded with this version
        if (clientUrl.searchParams.get('v') !== serverVersion) {
          clientUrl.searchParams.set('v', serverVersion);

          datadogRum.addError('Client reload', {
            type: 'SERVICE_WORKER',
            versionFrom: config.buildVersion,
            versionTo: serverVersion,
          });

          debugServiceWorker &&
            console.info(
              '[jane service worker] new version available, reloading'
            );
          window.location.href = clientUrl.href;
        }
      }
    },
    debug: debugServiceWorker,
  });
  const FlagProvider = await asyncFlagProvider(
    {
      baseUrl: config.launchDarklyBaseUrl,
      clientSideID: config.launchDarklyClientID,
      eventsUrl: config.launchDarklyEventsUrl,
      streamUrl: config.launchDarklyStreamUrl,
    },
    getFlagContext({ extraAttributes: { appMode: 'kiosk' } })
  );

  root.render(
    <ErrorBoundary
      legacy={true}
      trackError={datadogRum.addError}
      withReeferThemeProvider
    >
      <EcommAppProvider appMode="kiosk">
        <FlagProvider>
          <App />
          <div id="modal-root" />
        </FlagProvider>
      </EcommAppProvider>
    </ErrorBoundary>
  );
})();
